import { create } from 'zustand';

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  /**
   * Поточний tenant context. Зберігається у localStorage окремо від токенів,
   * бо JWT не містить tenant_id (Порція 3.0.2: tenant контекст — header).
   *
   * - `null` коли user щойно залогінився і ще не обрав tenant (бачить
   *   /select-tenant) або не має жодного membership (бачить /no-tenants).
   * - Виставляється: успішний login → перенаправлення → user обирає tenant
   *   у /select-tenant; tenant switcher у хедері; accept-invitation;
   *   create-tenant з /no-tenants.
   * - Скидається: logout, помилка «No active membership» з backend, явний
   *   перехід на /select-tenant.
   */
  activeTenantId: string | null;
  setTokens: (access: string, refresh: string) => void;
  setActiveTenant: (tenantId: string | null) => void;
  clear: () => void;
}

const ACCESS_KEY = 'auth.access';
const REFRESH_KEY = 'auth.refresh';
const TENANT_KEY = 'auth.active_tenant_id';

export const useAuth = create<AuthState>((set) => ({
  accessToken: localStorage.getItem(ACCESS_KEY),
  refreshToken: localStorage.getItem(REFRESH_KEY),
  activeTenantId: localStorage.getItem(TENANT_KEY),
  setTokens: (access, refresh) => {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    set({ accessToken: access, refreshToken: refresh });
  },
  setActiveTenant: (tenantId) => {
    if (tenantId === null) {
      localStorage.removeItem(TENANT_KEY);
    } else {
      localStorage.setItem(TENANT_KEY, tenantId);
    }
    set({ activeTenantId: tenantId });
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(TENANT_KEY);
    set({ accessToken: null, refreshToken: null, activeTenantId: null });
  },
}));

// Helper-функції для використання поза React-компонентами (api-клієнт).
export const getAccessToken = () => useAuth.getState().accessToken;
export const getRefreshToken = () => useAuth.getState().refreshToken;
export const getActiveTenantId = () => useAuth.getState().activeTenantId;
export const setTokens = (a: string, r: string) => useAuth.getState().setTokens(a, r);
export const setActiveTenant = (t: string | null) =>
  useAuth.getState().setActiveTenant(t);
export const clearTokens = () => useAuth.getState().clear();
